import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import AuthLayout from '../../components/AuthLayout';
import { toast } from 'react-toastify';
import methodModel from '../../methods/methods';

const AcceptInvite = () => {
    const history = useNavigate();
    const [form, setForm] = useState({ 
        firstName: '', 
        lastName: '', 
        password: '', 
        confirmPassword: '' 
    });
    const [inviteData, setInviteData] = useState<any>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [eyes, setEyes] = useState({ password: false, confirmPassword: false });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const token = methodModel.getPrams('token');

    useEffect(() => {
        if (!token) {
            setError('Invalid invitation link');
            setLoading(false);
            return;
        }

        // Verify the invitation token
        ApiClient.get(`api/invitation/verify?token=${token}`).then((res: any) => {
            if (res.success) {
                setInviteData(res.data);
                setForm({
                    ...form,
                    firstName: res.data.firstName || '',
                    lastName: res.data.lastName || ''
                });
            } else {
                setError(res.error?.message || 'Invalid or expired invitation');
            }
            setLoading(false);
        }).catch((err) => {
            setError('Failed to verify invitation');
            setLoading(false);
        });
    }, [token]);

    const handleSubmit = (e: any) => {
        e.preventDefault();
        setSubmitted(true);

        // Validate for new users
        if (!inviteData?.isExistingUser) {
            if (!form.password || form.password.length < 8) {
                toast.error('Password must be at least 8 characters');
                return;
            }
            if (form.password !== form.confirmPassword) {
                toast.error('Passwords do not match');
                return;
            }
            if (!agreedToTerms) {
                toast.error('You must agree to the Terms and Conditions');
                return;
            }
        }

        loader(true);

        const payload: any = { token };
        
        if (!inviteData?.isExistingUser) {
            payload.password = form.password;
            payload.firstName = form.firstName;
            payload.lastName = form.lastName;
        }

        ApiClient.post('api/invitation/accept', payload).then((res: any) => {
            loader(false);
            if (res.success) {
                toast.success(res.message || 'Successfully joined the group!');
                history('/login?email=' + encodeURIComponent(inviteData.email));
            } else {
                toast.error(res.error?.message || 'Failed to accept invitation');
            }
        }).catch((err) => {
            loader(false);
            toast.error('Failed to accept invitation');
        });
    };

    if (loading) {
        return (
            <AuthLayout>
                <div className="text-center py-10">
                    <p className="text-gray-600">Verifying invitation...</p>
                </div>
            </AuthLayout>
        );
    }

    if (error) {
        return (
            <AuthLayout>
                <div className="text-center py-10">
                    <h4 className="text-red-500 text-xl font-bold mb-4">Invitation Error</h4>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a href="/login" className="text-orange-500 hover:underline">Go to Login</a>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <form onSubmit={handleSubmit} autoComplete="off">
                <h4 className="text-typo mb-2 text-2xl font-bold">
                    {inviteData?.isExistingUser ? 'Join Group' : 'Complete Your Registration'}
                </h4>
                <p className="text-gray-600 mb-6">
                    You've been invited to join <strong className="text-orange-500">{inviteData?.groupName}</strong>
                    {inviteData?.inviterName && <> by {inviteData.inviterName}</>}
                </p>

                {/* Email - Read Only */}
                <div className="mb-3">
                    <label className="text-sm text-gray-600 mb-1 block">Email</label>
                    <input
                        type="email"
                        value={inviteData?.email || ''}
                        className="shadow-box border-1 border-gray-300 bg-gray-200 w-full text-sm rounded-lg h-12 px-3 cursor-not-allowed"
                        disabled
                    />
                </div>

                {/* Show name and password fields only for new users */}
                {!inviteData?.isExistingUser && (
                    <>
                        <div className="mb-3">
                            <label className="text-sm text-gray-600 mb-1 block">First Name</label>
                            <input
                                type="text"
                                value={form.firstName}
                                onChange={e => setForm({ ...form, firstName: e.target.value })}
                                className="shadow-box border-1 border-gray-300 bg-gray-100 w-full text-sm rounded-lg h-12 px-3"
                                placeholder="Enter First Name"
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="text-sm text-gray-600 mb-1 block">Last Name</label>
                            <input
                                type="text"
                                value={form.lastName}
                                onChange={e => setForm({ ...form, lastName: e.target.value })}
                                className="shadow-box border-1 border-gray-300 bg-gray-100 w-full text-sm rounded-lg h-12 px-3"
                                placeholder="Enter Last Name"
                                required
                            />
                        </div>

                        <div className="mb-3 relative">
                            <label className="text-sm text-gray-600 mb-1 block">Password</label>
                            <input
                                type={eyes.password ? 'text' : 'password'}
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                className="shadow-box border-1 border-gray-300 bg-gray-100 w-full text-sm rounded-lg h-12 px-3 pr-10"
                                placeholder="Create a password (min 8 characters)"
                                minLength={8}
                                required
                            />
                            <div className="absolute right-3 top-9 text-gray-500 cursor-pointer">
                                <i 
                                    className={eyes.password ? 'fa fa-eye' : 'fa fa-eye-slash'} 
                                    onClick={() => setEyes({ ...eyes, password: !eyes.password })}
                                />
                            </div>
                        </div>

                        <div className="mb-6 relative">
                            <label className="text-sm text-gray-600 mb-1 block">Confirm Password</label>
                            <input
                                type={eyes.confirmPassword ? 'text' : 'password'}
                                value={form.confirmPassword}
                                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                                className="shadow-box border-1 border-gray-300 bg-gray-100 w-full text-sm rounded-lg h-12 px-3 pr-10"
                                placeholder="Confirm your password"
                                minLength={8}
                                required
                            />
                            <div className="absolute right-3 top-9 text-gray-500 cursor-pointer">
                                <i 
                                    className={eyes.confirmPassword ? 'fa fa-eye' : 'fa fa-eye-slash'} 
                                    onClick={() => setEyes({ ...eyes, confirmPassword: !eyes.confirmPassword })}
                                />
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    className="mt-1 h-4 w-4 flex-shrink-0"
                                />
                               <span className="text-xs text-gray-600">
                                    I agree to the{' '}
                                    <a href="https://skillsnetpro.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Terms and Conditions</a>
                                </span>
                            </label>
                            {submitted && !agreedToTerms && (
                                <p className="text-red-500 text-xs mt-1">You must agree to the Terms and Conditions</p>
                            )}
                        </div>
                    </>
                )}

                {inviteData?.isExistingUser && (
                    <p className="text-sm text-gray-500 mb-6">
                        You already have an account. Click below to add this group to your profile.
                    </p>
                )}

                <button
                    type="submit"
                    className={`w-full text-sm font-medium text-white h-12 rounded-lg transition-all ${
                        !inviteData?.isExistingUser && !agreedToTerms
                            ? 'bg-orange-300 cursor-not-allowed opacity-60'
                            : 'bg-orange-500 hover:opacity-80'
                    }`}
                    disabled={!inviteData?.isExistingUser && !agreedToTerms}
                >
                    {inviteData?.isExistingUser ? 'Join Group' : 'Create Account & Join'}
                </button>

                <p className="text-sm mt-4 text-center text-gray-500">
                    Already have an account? <a href="/login" className="text-orange-500">Sign In</a>
                </p>
            </form>
        </AuthLayout>
    );
};

export default AcceptInvite;